import { useRef } from "react";
import { Bold, Heading, List } from "lucide-react";

// **gras** -> <strong>
const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
  part.startsWith("**") && part.endsWith("**") && part.length > 4 ? <strong key={i}>{part.slice(2, -2)}</strong> : part);

function Block({ text }) {
  const out = [];
  let list = [];
  let lines = [];
  const flushList = () => { if (list.length) { out.push(<ul key={out.length} className="list-disc pl-5 space-y-0.5">{list.map((x, i) => <li key={i}>{inline(x)}</li>)}</ul>); list = []; } };
  const flushLines = () => { if (lines.length) { out.push(<p key={out.length} className="whitespace-pre-line text-justify break-words hyphens-auto" lang="fr">{inline(lines.join("\n"))}</p>); lines = []; } };
  text.split("\n").forEach((raw) => {
    const st = raw.trim();
    if (/^#{1,3} /.test(st)) { flushLines(); flushList(); out.push(<p key={out.length} className="font-heading font-bold text-base text-primary">{inline(st.replace(/^#{1,3} /, ""))}</p>); }
    else if (/^[-*] /.test(st)) { flushLines(); list.push(st.slice(2)); }
    else { flushList(); lines.push(raw); }
  });
  flushLines(); flushList();
  return <div className="space-y-1">{out}</div>;
}

// Shows multi-line text as justified paragraphs (blank line = new paragraph), with **gras**, # titres and - listes.
export function FormattedText({ text, className = "", testid }) {
  const paras = String(text || "").replace(/\r\n?/g, "\n").split(/\n\s*\n/).map((p) => p.replace(/^\n+|\n+$/g, "")).filter((p) => p.trim());
  if (!paras.length) return null;
  return (
    <div className={`text-sm mt-2 space-y-3 leading-relaxed ${className}`} data-testid={testid}>
      {paras.map((p, i) => <Block key={i} text={p} />)}
    </div>
  );
}

// Toolbar Gras / Titre / Liste for a textarea holding FormattedText syntax.
export function FormatToolbar({ textareaRef, value, onChange, testid = "fmt" }) {
  const apply = (mode) => {
    const el = textareaRef.current;
    const s = el ? el.selectionStart : value.length;
    const e = el ? el.selectionEnd : value.length;
    let next, caret;
    if (mode === "bold") {
      const sel = value.slice(s, e) || "texte en gras";
      next = `${value.slice(0, s)}**${sel}**${value.slice(e)}`; caret = s + sel.length + 4;
    } else {
      const ls = value.lastIndexOf("\n", s - 1) + 1;
      const block = value.slice(ls, e);
      const prefix = mode === "title" ? "## " : "- ";
      const done = block.split("\n").map((l) => (l.startsWith(prefix) ? l : prefix + l.replace(/^(## |- )/, ""))).join("\n");
      next = value.slice(0, ls) + done + value.slice(e); caret = ls + done.length;
    }
    onChange(next);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret); } });
  };
  const B = ({ mode, Icon, label }) => (
    <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => apply(mode)} className="inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-xs hover:bg-muted" data-testid={`${testid}-${mode}`} title={label}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-1.5 mb-1.5">
      <B mode="bold" Icon={Bold} label="Gras" />
      <B mode="title" Icon={Heading} label="Titre" />
      <B mode="list" Icon={List} label="Liste" />
    </div>
  );
}

export const useTextareaRef = () => useRef(null);
